// AudioTranscription.jsx
import React, { useState , useRef, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { X, Mic, Download, Plus, FileText } from 'lucide-react';
import './transcription.css';
import { jsPDF } from 'jspdf';

const uploadLambdaUrl = 'https://heyogd26exnosx3kbxlwsvjgge0maafb.lambda-url.ap-southeast-1.on.aws/'; // presign + upload
const transcriptionUrl = 'https://q7grrnnp5xrpf4uwpazuv33jjm0awxrn.lambda-url.ap-southeast-1.on.aws/'; // diarize/transcribe trigger
const summaryLambdaUrl = 'https://mejku5sat6kok3o2mydndbirg40azgvh.lambda-url.ap-southeast-1.on.aws/'; //4o streamed

const LANG_OPTIONS = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'it', label: 'Italian' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'zh', label: 'Chinese' },
  { code: 'ja', label: 'Japanese' },
  { code: 'ko', label: 'Korean' }
];

const TRANSCRIPTION_MODES = {
  pyannote: 'pyannote',
  scribe: 'scribe'
};

const SCRIBE_MAX_SECONDS = 150 * 60; // 150 minutes, 2.5hours

// returns duration in seconds
function getAudioDuration(file, existingUrl) {
  return new Promise((resolve) => {
    const url = existingUrl || URL.createObjectURL(file);
    const audio = document.createElement('audio');
    audio.preload = 'metadata';

    const cleanup = () => {
      if (!existingUrl) {
        try { URL.revokeObjectURL(url); } catch {}
      }
      audio.remove();
    };

    audio.onloadedmetadata = () => {
      const d = Number.isFinite(audio.duration) ? audio.duration : 0;
      cleanup();
      resolve(d);
    };
    audio.onerror = () => { cleanup(); resolve(Infinity); };
    audio.src = url;
  });
}


function timeConvert(totalSeconds) {
  const secs = Math.floor(totalSeconds);
  const hours = Math.floor(secs / 3600);
  const mins = Math.floor((secs % 3600) / 60);
  const remSecs = secs % 60;

  const mm = String(mins).padStart(2, '0');
  const ss = String(remSecs).padStart(2, '0');

  return hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`;
}

function hmsmsToSeconds(hms) {
  // "HH:MM:SS.mmm" -> seconds (number)
  const m = hms.match(/^(\d{2}):(\d{2}):(\d{2})\.(\d{3})$/);
  if (!m) return 0;
  const [, hh, mm, ss, ms] = m;
  return (+hh) * 3600 + (+mm) * 60 + (+ss) + (+ms) / 1000;
}

function parseSpeakeredTxt(speakeredTxt = "") {
  // returns [{start,end,speaker,text}]
  const lines = speakeredTxt.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  const re = /^\[(\d{2}:\d{2}:\d{2}\.\d{3})\s*-\s*(\d{2}:\d{2}:\d{2}\.\d{3})\]\s+([^:]+):\s*(.+)$/;
  const out = [];
  for (const line of lines) {
    const m = line.match(re);
    if (!m) {
      // fallback: attach to previous block if present
      if (out.length) out[out.length - 1].text += (out[out.length - 1].text ? " " : "") + line;
      continue;
    }
    const [, s1, s2, speakerRaw, text] = m;
    out.push({
      start: hmsmsToSeconds(s1),
      end:   hmsmsToSeconds(s2),
      speaker: speakerRaw.trim(),
      text: (text || "").trim()
    });
  }
  return out;
}

function groupConsecutiveBySpeaker(rows) {
  // merges consecutive lines by the same speaker into paragraphs
  const groups = [];
  for (const r of rows) {
    const last = groups[groups.length - 1];
    if (last && last.speaker === r.speaker) {
      last.end = r.end;
      last.text = (last.text ? last.text + " " : "") + r.text;
    } else {
      groups.push({ ...r });
    }
  }
  return groups;
}

function parseScribeResponse(data) {
  try {
    const { 
      text,
      words = [],
      language_code,
      additional_formats = []
    } = data;

    // Convert Scribe's word-level data to our segment format
    const segments = words
      .filter(w => w.type === 'word' && w.speaker_id)
      .map(w => ({
        start: w.start,
        end: w.end,
        speaker: w.speaker_id,
        text: w.text
      }));

    // Find PDF if available
    const pdfFormat = additional_formats.find(f => 
      f.requested_format === 'pdf' && 
      f.content_type === 'application/pdf'
    );

    return {
      text,
      segments,
      language: language_code,
      pdf: pdfFormat?.content, // Base64 or URL to PDF
      isScribeFormat: true
    };
  } catch (err) {
    console.error('Error parsing Scribe response:', err);
    return null;
  }
}

const AudioTranscription = ({ isOpen, onClose }) => {
  const [audioFiles, setAudioFiles] = useState([]);
  const [transcriptions, setTranscriptions] = useState({});
  const [rawResponses, setRawResponses] = useState({});
  const [filePrompts, setFilePrompts] = useState({});
  const [fileLanguages, setFileLanguages] = useState({});
  const [openAdditional, setOpenAdditional] = useState({});
  const [transcriptionMode, setTranscriptionMode] = useState(TRANSCRIPTION_MODES.pyannote);
  const pollingRefs = useRef({});
  const fileInputRef = useRef(null);
  const previewUrlsRef = useRef(new Set());
  const audioListRef = useRef(null);
  const prevCountRef = useRef(0);
  const durationsRef = useRef({}); // { [id]: seconds }
  const [summariesById, setSummariesById] = useState({});
  const [summaryStatus, setSummaryStatus] = useState({});

  async function summarizeTranscript(id, transcriptText) {
    if (!transcriptText || !transcriptText.trim()) return;

    setSummaryStatus(prev => ({ ...prev, [id]: 'loading' }));

    const MAX_CHARS = 16000;
    const clipped = transcriptText.length > MAX_CHARS
      ? transcriptText.slice(0, MAX_CHARS) + '\n\n[...clipped for length...]'
      : transcriptText;

    const userText = `
      You are a transcript summarizer. Produce a highly concise yet detailed summary from the transcript below.

      OUTPUT RULES (very important):
      - Plain text only. Do not use Markdown, asterisks, hashes, code blocks or tables.
      - Keep the transcript brief and useful. Avoid filler sentences and speculation.

      TRANSCRIPT START
      ${clipped}
      TRANSCRIPT END
      `.trim();

    async function callLambda(model) {
      // ✅ Send the payload itself, NOT { body: "<json>" }
      const payload = {
        model,
        messages: [
          { role: "user", content: [{ type: "text", text: userText }] }
        ],
        // your lambda sets stream:true itself; sending it here is harmless
        // stream: true,
      };

      const res = await fetch(summaryLambdaUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'text/plain' },
        body: JSON.stringify(payload)
      });

      // Your lambda streams plain text chunks and ends — so just read as text
      const text = await res.text();
      // If OpenAI error bubbles up from catch, lambda also returns plain text
      return { status: res.status, text };
    }

    try {
      // 1) try gpt-4o
      let { status, text } = await callLambda("gpt-4o");

      // 2) if the lambda rejects our model pre-check, retry with -mini
      if (/unsupported model selected/i.test(text || '')) {
        const r = await callLambda("gpt-4o-mini");
        status = r.status;
        text = r.text;
      }

      // Fallback message if totally empty
      const summaryText = (text && text.trim()) ? text.trim() : 'Summary generated, but no content was returned.';

      setSummariesById(prev => ({ ...prev, [id]: summaryText }));
      setSummaryStatus(prev => ({ ...prev, [id]: 'done' }));
    } catch (err) {
      setSummariesById(prev => ({ ...prev, [id]: `Error generating summary: ${err.message}` }));
      setSummaryStatus(prev => ({ ...prev, [id]: 'error' }));
    }
  }

  // Per-file status: { phase: 'idle'|'uploading'|'triggering'|'processing'|'done'|'error', uploadPct?:0-100, note?:string }
  const [statusById, setStatusById] = useState({});
  const setStatus = (id, partial) =>
    setStatusById((prev) => ({ ...prev, [id]: { ...(prev[id] || { phase: 'idle', uploadPct: 0 }), ...partial } }));
  const audioRefs = useRef({});

  function uploadWithProgress({ file, url, onProgress }) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', url);
      xhr.responseType = 'text';
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

      let lastEmit = 0;
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && typeof onProgress === 'function') {
          const pct = Math.round((e.loaded / e.total) * 100);
          const now = Date.now();
          if (pct === 100 || now - lastEmit > 100) { // emit at most every 100ms (and always emit 100)
            lastEmit = now;
            onProgress(pct);
          }
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`Upload failed (${xhr.status})`));
      };
      xhr.onerror = () => reject(new Error('Network error during upload'));
      xhr.onabort = () => reject(new Error('Upload aborted'));
      xhr.send(file);
    });
  }


  function startPolling({ id, bucket, key, intervalMs = 60000, maxAttempts = 60 }) {
    // Cancel any existing poll for this id first
    if (pollingRefs.current[id]) {
      clearTimeout(pollingRefs.current[id]);
      delete pollingRefs.current[id];
    }

    setStatus(id, { phase: 'processing', note: 'Waiting for transcript (polling)...' });

    let attempts = 0;
    const filename = (typeof key === 'string') ? key.split('/').pop() : undefined;

    const attempt = async () => {
      attempts += 1;
      try {
        // // Build inner payload with s3Key + filename (what the Lambda expects)
        // const innerPayload = { action: 'status', s3Key: key, filename, inline: true };

        const outerPayload = { action: 'status', bucket, key, inline: true };

        console.log('[startPolling] sending status ping (bucket/key)', { id, attempt: attempts, outerPayload });

        const res = await fetch(transcriptionUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(outerPayload) // <- single-encoded JSON string in request body
        });


        const text = await res.text().catch(() => null);
        console.log('[startPolling] response', { id, attempt: attempts, status: res.status, ok: res.ok, text });

        // Try to parse JSON (may be nested/stringified)
        let json = null;
        try { json = text ? JSON.parse(text) : null; } catch (e) { json = text; }

        // If the API returns the standard lambda wrapper { statusCode, body: "..." } unwrap it
        if (json && typeof json === 'object' && json.statusCode && typeof json.body === 'string') {
          try {
            const inner = JSON.parse(json.body);
            json = inner ?? json;
          } catch (e) {
            // leave json as-is if parsing fails
          }
        }

        // success condition: found flag or speakeredTxt present
        const speakeredTxt = String(json?.speakeredTxt ?? json?.body ?? json?.text ?? '');
        const foundFlag = json?.found === true;

        if (res.ok && (foundFlag || (typeof speakeredTxt === 'string' && speakeredTxt.trim().length > 0))) {
          // console.log('[startPolling] transcript found', { id, attempt: attempts });

          const parsedRows = parseSpeakeredTxt(speakeredTxt);
          const grouped = groupConsecutiveBySpeaker(parsedRows);

          setRawResponses(prev => ({ ...prev, [id]: json }));
          setTranscriptions(prev => ({
            ...prev,
            [id]: {
              text: speakeredTxt || '',
              segments: parsedRows,
              grouped,
              speakeredTxt,
              duration: json?.duration || 0,
              timestamp: new Date().toLocaleString()
            }
          }));
          const fullTextForSummary =
            speakeredTxt ||
            (grouped?.map(g =>
              `[${timeConvert(Math.round(g.start||0))}-${timeConvert(Math.round(g.end||0))}] ${g.speaker}: ${g.text}`
            ).join('\n')) ||
            '';
          summarizeTranscript(id, fullTextForSummary);

          setStatus(id, { phase: 'done', note: 'Transcript received.' });

          if (pollingRefs.current[id]) { clearTimeout(pollingRefs.current[id]); delete pollingRefs.current[id]; }
          return;
        }

        // 202 -> job started but not done yet: continue polling
        if (res.status === 202) {
          console.log('[startPolling] received 202 (job started) — scheduling next attempt', { id, attempt: attempts });
          if (attempts >= maxAttempts) {
            setStatus(id, { phase: 'error', note: 'Timed out waiting for transcript (202 responses).' });
            if (pollingRefs.current[id]) { clearTimeout(pollingRefs.current[id]); delete pollingRefs.current[id]; }
            return;
          }
          pollingRefs.current[id] = setTimeout(attempt, intervalMs);
          return;
        }

        // Not ready yet -> schedule next attempt or stop on max attempts
        if (attempts >= maxAttempts) {
          console.warn('[startPolling] max attempts reached', { id, attempts });
          setStatus(id, { phase: 'error', note: 'Timed out waiting for transcript.' });
          if (pollingRefs.current[id]) { clearTimeout(pollingRefs.current[id]); delete pollingRefs.current[id]; }
          return;
        }

        console.log('[startPolling] not ready yet — scheduling next attempt', { id, attempt: attempts });
        pollingRefs.current[id] = setTimeout(attempt, intervalMs);
      } catch (err) {
        console.error('[startPolling] error', { id, attempt: attempts, err });
        setStatus(id, { phase: 'error', note: err?.message || String(err) });
        if (pollingRefs.current[id]) { clearTimeout(pollingRefs.current[id]); delete pollingRefs.current[id]; }
      }
    };

    // start first attempt immediately
    attempt();
  }

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    const audioFormats = ['.mp3', '.wav', '.m4a', '.mp4'];
    const valid = files.filter((f) =>
      audioFormats.some((ext) => f.name.toLowerCase().endsWith(ext))
    );
    if (valid.length !== files.length) {
      alert('Some files were not supported and were skipped.');
    }

    const wrapped = valid.map((f) => {
      const previewUrl = URL.createObjectURL(f);
      previewUrlsRef.current.add(previewUrl);
      return { id: uuidv4(), file: f, previewUrl };
    });

    // prevent more than 2.5hours
    if (transcriptionMode === TRANSCRIPTION_MODES.scribe) {
      const checked = await Promise.all(
        wrapped.map(async (w) => ({
          ...w,
          duration: await getAudioDuration(w.file, w.previewUrl)
        }))
      );

      const allowed = checked.filter(w => w.duration <= SCRIBE_MAX_SECONDS);
      const rejected = checked.filter(w => w.duration > SCRIBE_MAX_SECONDS || !Number.isFinite(w.duration));

      if (rejected.length) {
        alert(
          `Scribe only supports up to 150 minutes (2.5 hours) per file.\n` +
          rejected.map(r => `${r.file.name}`).join('\n')
        );
        // clean up previews for rejected
        rejected.forEach(r => {
          try { URL.revokeObjectURL(r.previewUrl); } catch {}
          previewUrlsRef.current.delete(r.previewUrl);
        });
      }

      // cache durations for the allowed
      allowed.forEach(({ id, duration }) => { durationsRef.current[id] = duration; });

      setAudioFiles((prev) => [...prev, ...allowed]);
      allowed.forEach(({ id }) => setStatus(id, { phase: 'idle', uploadPct: 0, note: '' }));

    } else {
      // original path if not Scribe
      setAudioFiles((prev) => [...prev, ...wrapped]);
      wrapped.forEach(({ id }) => setStatus(id, { phase: 'idle', uploadPct: 0, note: '' }));
    }

    if (e.target) e.target.value = '';
    if (fileInputRef?.current) fileInputRef.current.value = '';
  };


  const transcribeAudio = async ({ id, file }) => {
    const prompt = filePrompts[id] || '';
    const language = fileLanguages[id] || 'en';

      // guard again if longer than 150 mins
    if (transcriptionMode === TRANSCRIPTION_MODES.scribe) {
      let duration = durationsRef.current[id];
      if (duration == null) {
        const previewUrl = audioFiles.find(x => x.id === id)?.previewUrl;
        duration = await getAudioDuration(file, previewUrl);
        durationsRef.current[id] = duration;
      }
      if (!Number.isFinite(duration) || duration > SCRIBE_MAX_SECONDS) {
        setStatus(id, { phase: 'error', note: 'File exceeds Scribe 150-minute limit.' });
        return;
      }
    }

    try {
      // 1) Ask presign Lambda for an S3 upload URL
      setStatus(id, { phase: 'uploading', uploadPct: 0, note: 'Requesting upload URL…' });
      const presignedRes = await fetch(uploadLambdaUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename: file.name,
          fileType: file.type || 'application/octet-stream',
          fileSize: file.size
        })
      });
      if (!presignedRes.ok) throw new Error('Failed to get S3 upload URL');
      const { uploadUrl, key } = await presignedRes.json();

      // 2) Upload the file directly to S3 (with progress)
      setStatus(id, { note: 'Uploading to S3…' });
      await uploadWithProgress({
        file,
        url: uploadUrl,
        onProgress: (pct) => setStatus(id, { uploadPct: pct })
      });
      setStatus(id, { uploadPct: 100, note: 'Upload complete.' });

      // 3) Trigger processing on the same transcription URL
      setStatus(id, { phase: 'triggering', note: 'Triggering processing…' });

      const payload = {
        action: 'process',
        filename: file.name,
        s3Key: key,
        language,
        prompt,
        inline: true,
        mode: transcriptionMode
      };

      console.log('Sending transcription request:', {
        url: transcriptionUrl,
        payload,
        headers: { 'Content-Type': 'application/json' }
      });

      const processRes = await fetch(transcriptionUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      // Read the body once as text (avoid "body stream already read" errors)
      const raw = await processRes.text().catch(() => null);
      console.log('Transcription lambda response (raw):', { status: processRes.status, ok: processRes.ok, raw });

      // Helper: attempt to JSON.parse up to a few layers to unwrap nested stringified JSON
      function tryDeepParse(input) {
        let cur = input;
        for (let i = 0; i < 3; i++) {
          if (cur === null || cur === undefined) break;
          if (typeof cur === 'string') {
            try {
              cur = JSON.parse(cur);
              continue;
            } catch {
              break;
            }
          } else {
            break;
          }
        }
        return cur;
      }

      let parsed = tryDeepParse(raw);

      // Handle Scribe mode (ElevenLabs) responses
      if (transcriptionMode === TRANSCRIPTION_MODES.scribe && parsed?.body) {
        try {
          const scribeData = JSON.parse(parsed.body);
          const formattedData = parseScribeResponse(scribeData);
          
          if (formattedData) {
            // Store PDF content if available
            if (formattedData.pdf) {
              setRawResponses(prev => ({
                ...prev,
                [id]: {
                  ...formattedData,
                  pdfContent: formattedData.pdf
                }
              }));
            }

            // Group consecutive segments by speaker
            const grouped = groupConsecutiveBySpeaker(formattedData.segments);

            setTranscriptions(prev => ({
              ...prev,
              [id]: {
                text: formattedData.text,
                segments: formattedData.segments,
                grouped,
                timestamp: new Date().toLocaleString(),
                language: formattedData.language
              }
            }));

            // Kick off summary after transcript is ready
            const fullTextForSummary = formattedData.text 
              || (grouped?.map(g => `[${timeConvert(Math.round(g.start||0))}-${timeConvert(Math.round(g.end||0))}] ${g.speaker}: ${g.text}`).join('\n')) 
              || '';
            summarizeTranscript(id, fullTextForSummary);


            setStatus(id, { 
              phase: 'done', 
              note: 'Transcript received with PDF.' 
            });
            return; // Exit early since we handled the Scribe format
          }
        } catch (err) {
          console.error('Error processing Scribe response:', err);
        }
      }

      // If content-type is JSON but tryDeepParse returned nothing useful, try one more time
      if (!parsed && processRes.headers.get('content-type')?.includes('application/json')) {
        try {
          parsed = JSON.parse(raw);
        } catch {}
      }

      // Handle the common Lambda/API Gateway wrapper: { statusCode: X, body: "..." }
      if (parsed && typeof parsed === 'object' && parsed.statusCode && typeof parsed.body === 'string') {
        parsed = tryDeepParse(parsed.body);
      }

      // FIXED: Handle 202 (Accepted) status - job started but still processing
      if (processRes.status === 202) {
        const data = parsed ?? {};
        const outputs = data?.outputs;
        
        if (outputs?.txtKey) {
          setStatus(id, { 
            phase: 'processing', 
            note: 'Job started on EC2 — polling for transcript.' 
          });
          startPolling({ 
            id, 
            bucket: outputs.bucket || '', 
            key: outputs.txtKey, 
            intervalMs: 60_000, 
            maxAttempts: 60 
          });
          setRawResponses((prev) => ({ ...prev, [id]: data }));
          return;
        } else {
          setStatus(id, { 
            phase: 'processing', 
            note: data?.message || 'Job started; awaiting results in S3.' 
          });
          setRawResponses((prev) => ({ ...prev, [id]: data }));
          return;
        }
      }

      // FIXED: Only throw error for non-OK status if it's not 202
      if (!processRes.ok && processRes.status !== 202) {
        const serverMsg = (parsed && (parsed.error || parsed.message)) || raw || `status ${processRes.status}`;
        throw new Error(`Processing Lambda failed (status=${processRes.status}): ${serverMsg}`);
      }

      const data = parsed ?? {};
      const outputs = data?.outputs;
      const hasInline = Boolean(data?.speakeredTxt || data?.text);

      if (outputs && !hasInline) {
        // Job completed but no inline text - start polling for the txtKey
        const bucket = outputs.bucket ?? uploadLambdaUrl?.bucket ?? null;
        const key = outputs.txtKey;
        if (!key) {
          setStatus(id, { phase: 'processing', note: data?.message || 'Job started; awaiting results in S3.' });
        } else {
          setStatus(id, { phase: 'processing', note: 'Job completed on EC2 — polling for transcript.' });
          startPolling({ id, bucket: bucket || '', key, intervalMs: 60_000, maxAttempts: 60 });
        }

        // store raw response and return (we'll populate transcriptions when polling finds file)
        setRawResponses((prev) => ({ ...prev, [id]: data }));
        return;
      }

      // server returned inline text or synchronous completion
      setStatus(id, { phase: 'processing', note: 'Processing completed…' });
      const speakeredTxt = data?.speakeredTxt || data?.text || '';
      const parsedRows = parseSpeakeredTxt(speakeredTxt);
      const grouped = groupConsecutiveBySpeaker(parsedRows);
      const maybeSegments = Array.isArray(data?.segments) ? data.segments : parsedRows;
      
      const msg = data?.message || 'Job completed.';

      setRawResponses((prev) => ({ ...prev, [id]: data }));
      setTranscriptions((prev) => ({
        ...prev,
        [id]: {
          text: speakeredTxt || `${msg}${
            outputs
              ? `\n\nBucket: ${outputs.bucket}\nRTTM: ${outputs.rttmKey}\nTXT: ${outputs.txtKey}\nSRT: ${outputs.srtKey}`
              : ''
          }`,
          segments: maybeSegments, 
          grouped, 
          speakeredTxt,
          duration: data?.duration || 0,
          timestamp: new Date().toLocaleString()
        }
      }));

      // Kick off summary after transcript is ready
      const fullTextForSummary = speakeredTxt || 
        (grouped?.map(g => `[${timeConvert(Math.round(g.start||0))}-${timeConvert(Math.round(g.end||0))}] ${g.speaker}: ${g.text}`).join('\n')) 
        || '';
      summarizeTranscript(id, fullTextForSummary);


      setStatus(id, { phase: 'done', note: speakeredTxt ? 'Transcript received.' : 'Artifacts ready in S3.' });
    } catch (error) {
      console.log('Transcription error:', error);
      setTranscriptions((prev) => ({
        ...prev,
        [id]: {
          text: `Error: ${error.message}`,
          segments: [],
          isError: true,
          timestamp: new Date().toLocaleString()
        }
      }));
      setStatus(id, { phase: 'error', note: error.message });
    }
  };


  const removeFile = (id) => {
    if (pollingRefs.current[id]) {
      clearTimeout(pollingRefs.current[id]);
      delete pollingRefs.current[id];
    }

    // revoke the URL for the file being removed
    const fileObj = audioFiles.find(item => item.id === id);
    if (fileObj && fileObj.previewUrl) {
      try { 
        URL.revokeObjectURL(fileObj.previewUrl);
        previewUrlsRef.current.delete(fileObj.previewUrl);
      } catch (e) { /* ignore */ }
    }

    setAudioFiles((prev) => prev.filter((item) => item.id !== id));
    setTranscriptions((prev) => { const c = { ...prev }; delete c[id]; return c; });
    setRawResponses((prev) => { const c = { ...prev }; delete c[id]; return c; });
    setFilePrompts((prev) => { const c = { ...prev }; delete c[id]; return c; });
    setFileLanguages((prev) => { const c = { ...prev }; delete c[id]; return c; });
    setOpenAdditional((prev) => { const c = { ...prev }; delete c[id]; return c; });
    setStatusById((prev) => { const c = { ...prev }; delete c[id]; return c; });

    // reset the file input element so the same file can be selected again
    if (fileInputRef?.current) fileInputRef.current.value = '';
  };


  useEffect(() => () => {
    Object.values(pollingRefs.current).forEach(t => clearTimeout(t));
    pollingRefs.current = {};
  }, []);


  useEffect(() => {
    return () => {
      previewUrlsRef.current.forEach(url => {
        try { URL.revokeObjectURL(url); } catch(e) {}
      });
      previewUrlsRef.current.clear();
      Object.values(pollingRefs.current).forEach(t => clearTimeout(t));
      pollingRefs.current = {};
    };
  }, []); // <-- important: empty deps so it only runs on unmount

  useEffect(() => {
  // only scroll when count increases (i.e. new file(s) added)
  if (audioFiles.length > prevCountRef.current) {
    const container = audioListRef.current;
    if (!container) {
      prevCountRef.current = audioFiles.length;
      return;
    }

    // Prefer scrolling the last item into view (more reliable if rows vary in height).
    const lastChild = container.lastElementChild;
    if (lastChild && typeof lastChild.scrollIntoView === 'function') {
      // Give the browser one paint to layout children (optional, but helps if heavy content)
      requestAnimationFrame(() => {
        lastChild.scrollIntoView({ behavior: 'smooth', block: 'end' });
      });
    } else {
      // Fallback: scroll container to bottom
      requestAnimationFrame(() => {
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      });
    }
  }

  prevCountRef.current = audioFiles.length;
}, [audioFiles.length]);


  const downloadTranscription = (id) => {
    const t = transcriptions[id];
    if (!t || t.isError) return;

    const groupedBlocks =
      (Array.isArray(t.grouped) && t.grouped.length > 0)
        ? t.grouped
        : groupConsecutiveBySpeaker(parseSpeakeredTxt(t.speakeredTxt || ''));

    const doc = new jsPDF({ unit: 'pt', format: 'letter' });
    const margin = 40;
    const lineH = 14;
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const maxW  = pageW - margin * 2;

    let y = margin;

    const fileName = audioFiles.find((x) => x.id === id)?.file.name || '';
    const headerLines = [
      `File: ${fileName}`,
      `Date: ${t.timestamp || new Date().toLocaleString()}`
    ];

    doc.setFont('helvetica', 'bold').setFontSize(12);
    headerLines.forEach((line) => {
      const wrapped = doc.splitTextToSize(line, maxW);
      wrapped.forEach((row) => {
        if (y + lineH > pageH - margin) { doc.addPage(); y = margin; }
        doc.text(row, margin, y); y += lineH;
      });
    });

    // Spacer
    y += 8;

    doc.setFont('helvetica', 'bold').setFontSize(13);
    if (y + lineH > pageH - margin) { doc.addPage(); y = margin; }
    doc.text('Summary of Transcript', margin, y); y += lineH;

    doc.setFont('helvetica', 'normal').setFontSize(11);
    const summary = summariesById[id] || '(Summary not available.)';
    const summaryLines = doc.splitTextToSize(summary, maxW);
    summaryLines.forEach((row) => {
      if (y + lineH > pageH - margin) { doc.addPage(); y = margin; }
      doc.text(row, margin, y); y += lineH;
    });

    // Extra space before transcript section
    y += 12;

    // === Transcript section header ===
    doc.setFont('helvetica', 'bold').setFontSize(13);
    if (y + lineH > pageH - margin) { doc.addPage(); y = margin; }
    doc.text('Transcript', margin, y); y += lineH;

    // If no grouped blocks, dump raw text
    if (!groupedBlocks || groupedBlocks.length === 0) {
      doc.setFont('helvetica', 'normal').setFontSize(11);
      const paragraphs = String(t.text || '').split(/\n\s*\n|(?<=\.)\s+/).map(s => s.trim()).filter(Boolean);
      paragraphs.forEach((para) => {
        const wrapped = doc.splitTextToSize(para, maxW);
        wrapped.forEach((row) => {
          if (y + lineH > pageH - margin) { doc.addPage(); y = margin; }
          doc.text(row, margin, y); y += lineH;
        });
        y += 8;
      });
      doc.save(`${fileName.replace(/\.[^/.]+$/, '')}_transcript.pdf`);
      return;
    }

    // Pretty speaker blocks
    groupedBlocks.forEach((blk) => {
      const speaker = blk.speaker || 'Unknown';
      const start   = timeConvert(Math.round(blk.start || 0));
      const end     = timeConvert(Math.round(blk.end ?? blk.start ?? 0));
      const title   = `${speaker}  (${start}–${end})`;

      doc.setFont('helvetica', 'bold').setFontSize(12);
      if (y + lineH > pageH - margin) { doc.addPage(); y = margin; }
      doc.text(title, margin, y); y += lineH;

      doc.setFont('helvetica', 'normal').setFontSize(11);
      const wrapped = doc.splitTextToSize(blk.text || '', maxW);
      wrapped.forEach((row) => {
        if (y + lineH > pageH - margin) { doc.addPage(); y = margin; }
        doc.text(row, margin, y); y += lineH;
      });

      y += 10;
    });
    doc.save(`${fileName.replace(/\.[^/.]+$/, '')}_transcript.pdf`);
  };

  const downloadMarkdown = (id) => {
    const t = transcriptions[id];
    if (!t || t.isError) return;

    // Prefer already-grouped blocks; otherwise, try to parse/group from raw
    const groupedBlocks =
      (Array.isArray(t.grouped) && t.grouped.length > 0)
        ? t.grouped
        : groupConsecutiveBySpeaker(parseSpeakeredTxt(t.speakeredTxt || ''));

    const fileName = audioFiles.find((x) => x.id === id)?.file.name || 'transcript';
    const base = fileName.replace(/\.[^/.]+$/, '');

    // Header
    let md = `# ${base} — Transcript\n\n`;
    md += `Date: ${t.timestamp || new Date().toLocaleString()}\n\n`;

    // Summary
    const summary = (summariesById[id] || '(Summary not available.)').trim();
    md += `## Summary\n\n${summary}\n\n`;

    // Transcript
    md += `## Transcript\n\n`;

    if (groupedBlocks && groupedBlocks.length > 0) {
      groupedBlocks.forEach((blk) => {
        const speaker = blk.speaker || 'Unknown';
        const start   = timeConvert(Math.round(blk.start || 0));
        const end     = timeConvert(Math.round(blk.end ?? blk.start ?? 0));
        md += `### ${speaker} (${start}–${end})\n\n`;
        md += `${(blk.text || '').trim()}\n\n`;
      });
    } else {
      md += `${(t.text || '').trim()}\n`;
    }

    const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${base}_transcript.md`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="audio-transcription-popup">
      <div className="audio-popup-content">
        <div className="audio-header">
          <h2>Audio Transcription</h2>
          <button className="close-button" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="model-toggle">
          <span style={{ color: transcriptionMode === TRANSCRIPTION_MODES.pyannote ? '#fff' : '#666' }}>
            Self-Hosted Transcription
          </span>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={transcriptionMode === TRANSCRIPTION_MODES.scribe}
              onChange={(e) => {
                const newMode = e.target.checked ? TRANSCRIPTION_MODES.scribe : TRANSCRIPTION_MODES.pyannote;
                setTranscriptionMode(newMode);
              }}
            />
            <span className="toggle-slider"></span>
          </label>
          <span style={{ color: transcriptionMode === TRANSCRIPTION_MODES.scribe ? '#fff' : '#666' }}>
            Commercial Transcription
          </span>
        </div>

        <div className="audio-upload-area">
          <input
            ref={fileInputRef}
            type="file"
            id="audio-input"
            multiple
            accept=".mp3,.wav,.m4a,.mp4"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />
          {audioFiles.length === 0 ? (
            <div
              className="audio-dropzone"
              onClick={() => fileInputRef.current?.click()}
            >
              <Mic size={32} style={{ margin: '0 auto' }} />
              <span>Upload Audio Files</span>
              <small>Supported: MP3, WAV, M4A, MP4</small>
            </div>
          ) : (
            <button
              className="add-more-btn"
              onClick={() => fileInputRef.current?.click()}
            >
              <Plus size={20} /> Add More Audio Files
            </button>
          )}
        </div>

        <div className="audio-files-list" ref={audioListRef}>
          {audioFiles.map(({ id, file, previewUrl }) => {
            const t = transcriptions[id];
            const status = statusById[id] || { phase: 'idle', uploadPct: 0, note: '' };
            const expanded = openAdditional[id];

            return (
              <div key={id} className="audio-file-item">
                <div className="audio-file-header">
                  <span className="file-name">{file.name}</span>
                  <div className="audio-file-actions">
                    {!t && (status.phase === 'idle' || status.phase === 'error' || status.phase === 'done') && (
                      <button
                        className="transcribe-btn"
                        onClick={() => transcribeAudio({ id, file })}
                        disabled={status.phase !== 'idle' && status.phase !== 'error' && status.phase !== 'done'}
                      >
                        Transcribe
                      </button>
                    )}
                    {/* <button
                      className="toggle-additional-btn"
                      onClick={() =>
                        setOpenAdditional((prev) => ({ ...prev, [id]: !prev[id] }))
                      }
                    >
                      Additional {expanded ? '▲' : '▼'}
                    </button> */}
                    {t && !t.isError && (
                      <button
                        className="download-btn"
                        onClick={() => downloadTranscription(id)}
                        title={summaryStatus[id] !== 'done' ? 'Summary is still generating…' : 'Download PDF'}
                        disabled={summaryStatus[id] !== 'done'}
                      >
                        <Download size={16} />
                      </button>
                    )}

                    {t && !t.isError && (
                      <button
                        className="download-btn"
                        onClick={() => downloadMarkdown(id)}
                        title={summaryStatus[id] !== 'done' ? 'Summary is still generating…' : 'Download Markdown'}
                        disabled={summaryStatus[id] !== 'done'}
                        style={{ marginLeft: 6 }}
                      >
                        <FileText size={16} />
                      </button>
                    )}
                    
                    <button className="remove-btn" onClick={() => removeFile(id)}>
                      <X size={16} />
                    </button>
                  </div>
                </div>

                {expanded && (
                  <div className="additional-content">
                    <input
                      type="text"
                      placeholder="Optional description of audio file"
                      value={filePrompts[id] || ''}
                      onChange={(e) =>
                        setFilePrompts((prev) => ({ ...prev, [id]: e.target.value }))
                      }
                    />
                    <select
                      value={fileLanguages[id] || 'en'}
                      onChange={(e) =>
                        setFileLanguages((prev) => ({
                          ...prev,
                          [id]: e.target.value
                        }))
                      }
                    >
                      {LANG_OPTIONS.map((opt) => (
                        <option key={opt.code} value={opt.code}>
                          {opt.label} ({opt.code})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {(status.phase !== 'idle') && (
                  <div className="processing-indicator">
                    {status.phase === 'uploading' && (
                    <div>
                    <div>🔼 Uploading… {status.uploadPct ?? 0}%</div>
                    <div className="progress-bar" style={{ height: 6, background: '#eee', borderRadius: 4, marginTop: 6 }}>
                    <div style={{ width: `${status.uploadPct || 0}%`, height: '100%', borderRadius: 4, background: '#4a90e2' }} />
                    </div>
                  </div>
                  )}
                    {status.phase === 'triggering' && <div> Processing…</div>}
                    {status.phase === 'processing' && <div>⏳ Processing on EC2…</div>}
                    {status.phase === 'error' && <div>❌ {status.note}</div>}
                  </div>
                )}

                {/* SUMMARY FIRST */}
                {t && (
                  <div className="transcription-summary">
                    <h4>Summary of Events</h4>

                    {summaryStatus[id] === 'loading' && (
                      <div className="processing-indicator">🧠 Summarizing…</div>
                    )}

                    {summaryStatus[id] === 'error' && (
                      <div className="summary-error">{summariesById[id]}</div>
                    )}

                    {summaryStatus[id] === 'done' && (
                      <pre>{summariesById[id]}</pre>
                    )}

                    {!summaryStatus[id] && <div className="processing-indicator">🧠 Waiting for transcript…</div>}
                  </div>
                )}

                {t && summaryStatus[id] === 'done' && (
                  <div className={`transcription-result ${t.isError ? 'error' : ''}`}>
                    <div className="transcription-text">
                      {Array.isArray(t.grouped) && t.grouped.length > 0 ? (
                        <div className="pretty-transcript">
                          <h4>Transcript</h4>
                          <ul className="speaker-list">
                            {t.grouped.map((blk, idx) => {
                              const start = timeConvert(blk.start || 0);
                              const end = timeConvert(blk.end || (blk.start || 0));
                              const colorSeed = (blk.speaker || 'Unknown').replace(/\W/g, '');
                              const hue = (colorSeed.split('')
                                .reduce((a, c) => a + c.charCodeAt(0), 0) % 360);

                              const seekTo = (sec) => {
                                const audio = audioRefs.current[id];
                                if (audio) { audio.currentTime = sec; audio.play().catch(()=>{}); }
                              };

                              return (
                                <li
                                  key={idx}
                                  className="speaker-block"
                                  style={{ '--spk-h': `${hue}` }}
                                >
                                  <div className="speaker-meta">
                                    <span className="speaker-name">{blk.speaker || 'Unknown'}</span>
                                    <span className="time-chip" onClick={() => seekTo(blk.start || 0)}>
                                      {start}–{end}
                                    </span>
                                  </div>
                                  <p className="speaker-text">{blk.text}</p>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      ) : (
                        <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                          {t.text || 'Processing…'}
                        </pre>
                      )}
                    </div>
                  </div>
                )}
                
                <audio
                  key={id}
                  controls
                  className="audio-preview"
                  src={previewUrl}            // stable URL
                  ref={(el) => { audioRefs.current[id] = el; }}
                />

              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AudioTranscription;
